import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import FilterPanel from './components/FilterPanel';
import SearchBar from './components/SearchBar';
import ResultsHeader from './components/ResultsHeader';
import HelperCard from './components/HelperCard';
import EmptyState from './components/EmptyState';
import QuickBookModal from './components/QuickBookModal';
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';
import { useHelperData, useHelperSearch } from './hooks/useHelperData';

const HelperDiscovery = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHelper, setSelectedHelper] = useState(null);
  const [isQuickBookModalOpen, setIsQuickBookModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    helperType: 'all',
    expertise: 'all',
    language: 'all',
    availability: 'all'
  });

  const { helpers, loading, error, loadHelpers } = useHelperData(filters);
  const filteredHelpers = useHelperSearch(helpers, searchQuery);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <CrisisResourceBanner />
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Your Helper</h1>
          <p className="text-gray-600">Browse available helpers and find the right match for your needs</p>
        </div>

        <SearchBar 
          searchQuery={searchQuery} 
          onSearchChange={setSearchQuery}
          onSearch={() => {}}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
          <div className="lg:col-span-1">
            <FilterPanel 
              filters={filters} 
              onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
              onClearFilters={() => setFilters({
                helperType: 'all',
                expertise: 'all',
                language: 'all',
                availability: 'all'
              })}
            />
          </div>

          <div className="lg:col-span-3">
            {loading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState error={error} onRetry={loadHelpers} />
            ) : filteredHelpers?.length === 0 ? (
              <EmptyState 
                onClearFilters={() => setFilters({
                  helperType: 'all',
                  expertise: 'all',
                  language: 'all',
                  availability: 'all'
                })}
                onReturnToIntake={() => navigate('/intake')}
              />
            ) : (
              <>
                <ResultsHeader 
                  count={filteredHelpers?.length} 
                  totalResults={filteredHelpers?.length}
                  sortBy="relevance"
                  onSortChange={() => {}}
                />
                <div className="space-y-6">
                  {filteredHelpers?.map((helper) => (
                    <HelperCard
                      key={helper?.id}
                      helper={helper}
                      onViewProfile={() => navigate(`/helper-profile/${helper?.id}`)}
                      onQuickBook={() => {
                        setSelectedHelper(helper);
                        setIsQuickBookModalOpen(true);
                      }}
                      onSchedule={() => navigate('/booking-scheduling', { state: { selectedHelper: helper } })}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isQuickBookModalOpen && selectedHelper && (
        <QuickBookModal
          helper={selectedHelper}
          onClose={() => {
            setIsQuickBookModalOpen(false);
            setSelectedHelper(null);
          }}
          onConfirm={() => {
            setIsQuickBookModalOpen(false);
            navigate('/booking-scheduling', { state: { selectedHelper } });
          }}
        />
      )}
    </div>
  );
};

export default HelperDiscovery;